interface CategoryStyle {
    badgeClassName: string;
    /** CSS hue angle feeding the `--cover-*` tokens. See `COVER_HUES`. */
    coverHue: number;
}

const CATEGORY_TONES = {
    sky: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-300',
    blue: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-400/25 dark:bg-blue-400/10 dark:text-blue-300',
    violet: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-400/25 dark:bg-violet-400/10 dark:text-violet-300',
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-300',
    amber: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-300',
    rose: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/25 dark:bg-rose-400/10 dark:text-rose-300',
    purple: 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-400/25 dark:bg-purple-400/10 dark:text-purple-300',
    orange: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-400/25 dark:bg-orange-400/10 dark:text-orange-300',
    emerald:
        'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-300',
    green: 'border-green-200 bg-green-50 text-green-700 dark:border-green-400/25 dark:bg-green-400/10 dark:text-green-300',
    lime: 'border-lime-200 bg-lime-50 text-lime-700 dark:border-lime-400/25 dark:bg-lime-400/10 dark:text-lime-300',
    pink: 'border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-400/25 dark:bg-pink-400/10 dark:text-pink-300',
    yellow: 'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-400/25 dark:bg-yellow-400/10 dark:text-yellow-300',
    teal: 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-400/25 dark:bg-teal-400/10 dark:text-teal-300',
    fuchsia:
        'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-400/25 dark:bg-fuchsia-400/10 dark:text-fuchsia-300',
    slate: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-500/25 dark:bg-slate-500/10 dark:text-slate-300',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-400/25 dark:bg-cyan-400/10 dark:text-cyan-300',
};

type CategoryTone = keyof typeof CATEGORY_TONES;

/**
 * Each tone's hue angle, so generated cover art can be tinted with the same
 * colour the badge and accent bar already use for that category.
 *
 * These are Tailwind's own hue angles for the matching `*-400` shade — the art
 * has to sit beside the badge without reading as a near-miss. Saturation and
 * lightness come from the shared `--cover-*` tokens rather than from here, which
 * is what keeps the plates theme-aware.
 */
const COVER_HUES: Record<CategoryTone, number> = {
    sky: 199,
    blue: 213,
    violet: 258,
    indigo: 239,
    amber: 38,
    rose: 350,
    purple: 271,
    orange: 25,
    emerald: 160,
    green: 142,
    lime: 83,
    pink: 330,
    yellow: 48,
    teal: 174,
    fuchsia: 292,
    slate: 215,
    cyan: 189,
};

function getToneClassName(tone: CategoryTone): string {
    return CATEGORY_TONES[tone];
}

export function getCategoryHue(category?: string | null): number {
    return getCategoryStyle(category).coverHue;
}

export const CATEGORY_ORDER = [
    'Ansiedade',
    'Ansiedade social',
    'TOC',
    'Depressão',
    'Transtorno bipolar',
    'TDAH',
    'Álcool e substâncias',
    'Trauma e estresse',
    'Autismo',
    'Impulsividade',
    'Regulação emocional',
    'Autoestima',
    'Transtornos alimentares',
    'Bem-estar',
    'Funcionalidade',
    'Comportamento infantil',
    'Estilos parentais',
    'Perfeccionismo',
    'Rastreamento geral',
];

function createCategoryStyle(tone: CategoryTone): CategoryStyle {
    return {
        badgeClassName: getToneClassName(tone),
        coverHue: COVER_HUES[tone],
    };
}

/**
 * Every entry of `CATEGORY_ORDER` needs a tone: the assignment picker puts a
 * category chip on every row, so a category falling through to the default
 * reads as a bug rather than as a neutral choice.
 *
 * There are more categories than distinct tones, so two of them borrow a tone
 * already in use. What makes a borrowed tone safe is distance in
 * `CATEGORY_ORDER`, since that ordering is also the list's sort order:
 * 'Regulação emocional' takes violet seven places from 'Depressão', 'Estilos
 * parentais' takes amber ten places from 'Álcool e substâncias', and neither
 * collides with the category directly above or below it.
 */
const CATEGORY_STYLES: Record<string, CategoryStyle> = {
    Ansiedade: createCategoryStyle('sky'),
    'Ansiedade social': createCategoryStyle('blue'),
    TOC: createCategoryStyle('lime'),
    Depressão: createCategoryStyle('violet'),
    'Transtorno bipolar': createCategoryStyle('green'),
    TDAH: createCategoryStyle('indigo'),
    'Álcool e substâncias': createCategoryStyle('amber'),
    'Trauma e estresse': createCategoryStyle('rose'),
    Autismo: createCategoryStyle('purple'),
    Impulsividade: createCategoryStyle('orange'),
    'Regulação emocional': createCategoryStyle('violet'),
    Autoestima: createCategoryStyle('emerald'),
    'Transtornos alimentares': createCategoryStyle('pink'),
    'Bem-estar': createCategoryStyle('yellow'),
    Funcionalidade: createCategoryStyle('teal'),
    'Comportamento infantil': createCategoryStyle('fuchsia'),
    'Estilos parentais': createCategoryStyle('amber'),
    Perfeccionismo: createCategoryStyle('slate'),
    'Rastreamento geral': createCategoryStyle('cyan'),
};

const DEFAULT_CATEGORY_STYLE = createCategoryStyle('slate');

export function getCategoryStyle(category?: string | null): CategoryStyle {
    return (category && CATEGORY_STYLES[category]) || DEFAULT_CATEGORY_STYLE;
}
